import { useCallback, useState } from 'react';
import { subscriptionGateway } from '../index';
import type { BillingAccountResponse } from '../types';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { useBilling } from './BillingContext';
import { billingErrorMessage, isPermissionDenied } from './billingErrors';
import { useResource } from './useResource';

/** Ported from EPN-80's Billing Management screen. The billing contact is who billing questions and (V1: provider)
 * receipts go to. A save the server refuses with a plain 403 switches the whole page to its sticky read-only view. */
export function BillingContactSection() {
  const { readOnly } = useBilling();
  const [saved, setSaved] = useState<BillingAccountResponse | null | undefined>(undefined);
  const load = useCallback((signal: AbortSignal) => subscriptionGateway.getBillingAccount(signal), []);
  const { state, reload } = useResource(load, 'Could not load the billing contact.');

  return (
    <section aria-labelledby="billing-contact-heading" className="billing-section" data-testid="billing-contact">
      <h3 id="billing-contact-heading">Billing contact</h3>

      {state.status === 'loading' && <p className="muted" role="status">Loading…</p>}
      {state.status === 'denied' && <AccessDeniedNotice requiredPermissionLabel="Billing: View" />}
      {state.status === 'unavailable' && <p className="muted">Billing is not available in this environment.</p>}
      {state.status === 'error' && <RetryableError message={state.message} onRetry={reload} />}

      {state.status === 'ready' && (
        readOnly
          ? <ReadOnlyContact account={saved === undefined ? state.data : saved} />
          : <ContactForm account={saved === undefined ? state.data : saved} onSaved={setSaved} />
      )}
    </section>
  );
}

function ReadOnlyContact({ account }: { account: BillingAccountResponse | null }) {
  if (!account) return <EmptyState message="No billing contact has been set yet." />;
  return (
    <div className="read-only-notice" role="status">
      <p className="muted">You have read-only access to billing — changes must be made by a user with billing-management permission.</p>
      <dl aria-label="Billing contact">
        <div><dt>Name</dt><dd>{account.billingContactName}</dd></div>
        <div><dt>Email</dt><dd>{account.billingContactEmail}</dd></div>
      </dl>
    </div>
  );
}

function ContactForm({ account, onSaved }: { account: BillingAccountResponse | null; onSaved: (updated: BillingAccountResponse) => void }) {
  const { markReadOnly } = useBilling();
  const [name, setName] = useState(account?.billingContactName ?? '');
  const [email, setEmail] = useState(account?.billingContactEmail ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [showForm, setShowForm] = useState(account !== null);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await subscriptionGateway.updateBillingAccount({ billingContactName: name, billingContactEmail: email });
      onSaved(updated);
      setSaved(true);
    } catch (err) {
      if (isPermissionDenied(err)) {
        markReadOnly();
        return;
      }
      setError(billingErrorMessage(err, 'Could not save the billing contact.'));
    } finally {
      setBusy(false);
    }
  }

  if (!showForm) {
    return (
      <EmptyState
        message="No billing contact has been set yet."
        action={<button type="button" onClick={() => setShowForm(true)}>Add a billing contact</button>}
      />
    );
  }

  return (
    <div>
      {error && <p className="error" role="alert">{error}</p>}
      {saved && !error && <p className="success" role="status">Billing contact saved.</p>}

      <label htmlFor="billing-contact-name">Name</label>
      <input id="billing-contact-name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
      <label htmlFor="billing-contact-email">Email</label>
      <input id="billing-contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />

      <button type="button" disabled={busy || !name.trim() || !email.trim()} onClick={() => void save()}>
        {busy ? 'Saving…' : 'Save billing contact'}
      </button>
    </div>
  );
}
